import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";
import { ROLE_LABELS } from "@/context/AuthContext";
import { inputClass } from "@/components/FieldInput";
import { PageHeader, Card, StatusBadge } from "@/components/Ui";
import { Button } from "@/components/ui/button";

export default function AccessReview() {
  const [d, setD] = useState(null);
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState("Diritti confermati");
  const load = useCallback(() => api.get("/access-review").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const record = async () => {
    if (notes.trim().length < 5) return toast.error("Descrivi l'esito del riesame");
    try { await api.post("/access-review", { notes, outcome }); toast.success("Riesame registrato"); setNotes(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const suspend = async () => {
    try { const { data } = await api.post("/access-review/suspend-inactive"); toast.success(`${data.suspended} utenze sospese`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const inactive = d?.users.filter((u) => u.inactive_over_90) || [];

  return (
    <div>
      <PageHeader eyebrow="TM-ISMS-06 §9 · A.5.18" title="Riesame periodico degli accessi" subtitle="Riesame semestrale: verifica per ogni utenza della necessità operativa, del profilo e dei Trust collegati. Le utenze inattive da oltre 90 giorni vanno sospese.">
        <Button variant="outline" disabled={!inactive.length} onClick={suspend} data-testid="review-suspend-inactive">Sospendi inattive ({inactive.length})</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 overflow-x-auto">
          <table className="w-full text-sm text-left" data-testid="review-users-table">
            <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-3">Utente</th><th className="px-4 py-3">Profilo</th><th className="px-4 py-3">Trust</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3">Inattività</th><th className="px-4 py-3">Scadenza</th></tr>
            </thead>
            <tbody>
              {d?.users.map((u) => (
                <tr key={u.id} className={`border-b border-slate-100 ${u.inactive_over_90 ? "bg-amber-50/60" : ""}`}>
                  <td className="px-4 py-2.5"><p className="font-medium">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></td>
                  <td className="px-4 py-2.5 text-xs">{ROLE_LABELS[u.role]}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600 max-w-[200px] truncate">{["admin", "gestore_utenti"].includes(u.role) ? "Tutti" : u.trust_names.join(", ") || "—"}</td>
                  <td className="px-4 py-2.5"><StatusBadge value={u.status} /></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{u.inactive_days ?? "—"} gg</td>
                  <td className="px-4 py-2.5 text-xs">{fmtDate(u.access_expires_on)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="font-heading font-semibold mb-3">Registra riesame</h3>
            <select className={`${inputClass} mb-3`} value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="review-outcome-select">
              <option>Diritti confermati</option><option>Diritti confermati con correzioni</option><option>Rilevate anomalie</option>
            </select>
            <textarea className={`${inputClass} h-28 py-2 mb-3`} placeholder="Esito, utenze modificate, azioni correttive…" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="review-notes-input" />
            <Button className="w-full bg-[#0A2540] hover:bg-[#031B33] text-white" onClick={record} data-testid="review-record-button">Registra verbale di riesame</Button>
          </Card>
          <Card className="p-5">
            <h3 className="font-heading font-semibold mb-3">Storico riesami</h3>
            <div className="space-y-3" data-testid="review-history">
              {d?.history.length === 0 && <p className="text-sm text-slate-400">Nessun riesame registrato</p>}
              {d?.history.map((h) => (
                <div key={h.id} className="text-sm border-l-2 border-sky-400 pl-3">
                  <p className="font-medium">{h.outcome}</p>
                  <p className="text-xs text-slate-500">{fmtDateTime(h.created_at)} · {h.reviewer} · {h.active_users} utenze attive</p>
                  <p className="text-xs text-slate-600 mt-1">{h.notes}</p>
                  <p className="text-[11px] text-slate-400">Prossimo riesame entro {fmtDate(h.next_review_due)}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
